/**
 * ProgressGallery — Chronological photo grid with upload, lightbox, and delete.
 *
 * Reusable across entity types:
 *   - entityType="project" → Impact tab Progress Gallery
 *   - entityType="user"    → User Profile photo gallery (future)
 *   - entityType="community" → Community gallery (future)
 *
 * Upload pipeline:
 *   1. User picks files (JPEG/PNG, ≤ 10 MB, max 10 per batch)
 *   2. Each file is compressed client-side (≤ 500 KB, 1600px max dim)
 *   3. Compressed blob uploaded to MinIO via uploadFile()
 *   4. Media link record created via POST /api/MediaLinks
 *   5. New photo appears at top of grid
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { MediaLinkDto, MediaEntityType } from '../../api/mediaLinks';
import { fetchMediaLinks, createMediaLink, deleteMediaLink, updateMediaLink } from '../../api/mediaLinks';
import { uploadFile } from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import { compressImage } from '../../lib/compressImage';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import PhotoLightbox from './PhotoLightbox';
import styles from './ProgressGallery.module.css';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024; // 10 MB raw input limit
const MAX_BATCH_SIZE = 10;                     // Max files per upload
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ProgressGalleryProps {
  entityType: MediaEntityType;
  entityGuid: string;
  canUpload: boolean;
  maxPhotos?: number;
  debugMode?: boolean;
  /** Override the default "Progress Photos" section title (e.g., "Photos" for user profiles). */
  titleOverride?: string;
  /** Override empty-state title (default: "No Progress Photos"). */
  emptyTitleOverride?: string;
  /** Override empty-state hint (default: project-specific hint). */
  emptyHintOverride?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ProgressGallery({
  entityType,
  entityGuid,
  canUpload,
  maxPhotos = 100,
  debugMode,
  titleOverride,
  emptyTitleOverride,
  emptyHintOverride,
}: ProgressGalleryProps) {
  const { t } = useAppTranslation('pages');

  // ── State ──────────────────────────────────────────────────
  const [photos, setPhotos] = useState<MediaLinkDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0); // files currently processing
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Context menu + caption editing
  const [menuOpenGuid, setMenuOpenGuid] = useState<string | null>(null);
  const [editingCaptionGuid, setEditingCaptionGuid] = useState<string | null>(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasFetched = useRef(false);

  // ── Fetch photos on mount ──────────────────────────────────
  useEffect(() => {
    if (!entityGuid || hasFetched.current) return;
    hasFetched.current = true;

    setLoading(true);
    fetchMediaLinks(entityType, entityGuid, 'photo')
      .then((data) => {
        setPhotos(data);
        setError(null);
      })
      .catch((err) => {
        console.error('[ProgressGallery] Fetch failed:', err);
        setError(err.message || 'Failed to load photos');
      })
      .finally(() => setLoading(false));
  }, [entityType, entityGuid]);

  // ── Upload handler ─────────────────────────────────────────
  const handleUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      // Validate batch size
      const batch = Array.from(files).slice(0, MAX_BATCH_SIZE);

      // Check limit
      if (photos.length + batch.length > maxPhotos) {
        setError(
          t('progressGallery.limitReached', { max: maxPhotos })
        );
        return;
      }

      setUploading(true);
      setUploadCount(batch.length);
      setError(null);

      const newPhotos: MediaLinkDto[] = [];
      const errors: string[] = [];

      for (const file of batch) {
        // Validate type
        if (!ACCEPTED_TYPES.includes(file.type)) {
          errors.push(`${file.name}: ${t('progressGallery.invalidType')}`);
          setUploadCount((c) => c - 1);
          continue;
        }

        // Validate raw size
        if (file.size > MAX_RAW_FILE_BYTES) {
          errors.push(`${file.name}: ${t('progressGallery.tooLarge')}`);
          setUploadCount((c) => c - 1);
          continue;
        }

        try {
          // Compress full image (≤ 1600px, ~500 KB)
          const { blob, width, height } = await compressImage(file);

          // Generate thumbnail (≤ 300px, ~15–25 KB)
          const thumb = await compressImage(file, {
            maxDimension: 300,
            quality: 0.75,
            maxBytes: 30_000,
          });

          // Upload full image + thumbnail to MinIO
          const ts = Date.now();
          const slug = Math.random().toString(36).slice(2, 6);
          const fileName = `photo-${ts}-${slug}.jpg`;
          const thumbName = `thumb-${ts}-${slug}.jpg`;

          const [uploadResult, thumbResult] = await Promise.all([
            uploadFile(blob, 'progress', entityGuid, fileName),
            uploadFile(thumb.blob, 'progress', entityGuid, thumbName),
          ]);

          // Create media link record with both keys
          const mediaLink = await createMediaLink({
            entityType,
            entityGuid,
            mediaType: 'photo',
            storageKey: uploadResult.key,
            thumbnailKey: thumbResult.key,
            fileSizeBytes: blob.size,
            contentType: 'image/jpeg',
            widthPx: width,
            heightPx: height,
          });

          newPhotos.push(mediaLink);
        } catch (err: any) {
          console.error(`[ProgressGallery] Upload failed for ${file.name}:`, err);
          errors.push(`${file.name}: ${err.message || 'Upload failed'}`);
        }

        setUploadCount((c) => c - 1);
      }

      // Prepend new photos (newest first)
      if (newPhotos.length > 0) {
        setPhotos((prev) => [...newPhotos.reverse(), ...prev]);
      }

      if (errors.length > 0) {
        setError(errors.join('\n'));
      }

      setUploading(false);
      setUploadCount(0);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [entityType, entityGuid, photos.length, maxPhotos, t]
  );

  // ── Delete handler ─────────────────────────────────────────
  const handleDelete = useCallback(
    async (mediaLinkGuid: string) => {
      try {
        await deleteMediaLink(mediaLinkGuid);
        setPhotos((prev) =>
          prev.filter(
            (p) => p.mediaLinkGuid.toUpperCase() !== mediaLinkGuid.toUpperCase()
          )
        );
      } catch (err: any) {
        console.error('[ProgressGallery] Delete failed:', err);
        setError(err.message || 'Delete failed');
      }
    },
    []
  );

  // ── Caption handlers ──────────────────────────────────────
  const handleSaveCaption = useCallback(
    async (mediaLinkGuid: string, newLabel: string) => {
      setSavingCaption(true);
      try {
        const updated = await updateMediaLink(mediaLinkGuid, { label: newLabel || undefined });
        setPhotos((prev) =>
          prev.map((p) =>
            p.mediaLinkGuid.toUpperCase() === mediaLinkGuid.toUpperCase() ? updated : p
          )
        );
        setEditingCaptionGuid(null);
      } catch (err: any) {
        console.error('[ProgressGallery] Caption save failed:', err);
        setError(err.message || 'Caption save failed');
      } finally {
        setSavingCaption(false);
      }
    },
    []
  );

  const handleRemoveCaption = useCallback(
    async (mediaLinkGuid: string) => {
      setSavingCaption(true);
      try {
        const updated = await updateMediaLink(mediaLinkGuid, { label: '' });
        setPhotos((prev) =>
          prev.map((p) =>
            p.mediaLinkGuid.toUpperCase() === mediaLinkGuid.toUpperCase()
              ? { ...p, label: null }
              : p
          )
        );
        setMenuOpenGuid(null);
      } catch (err: any) {
        console.error('[ProgressGallery] Caption remove failed:', err);
        setError(err.message || 'Caption remove failed');
      } finally {
        setSavingCaption(false);
      }
    },
    []
  );

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenGuid) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenGuid(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpenGuid]);

  // ── Helpers ────────────────────────────────────────────────
  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const atLimit = photos.length >= maxPhotos;

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className={styles.section}>
      {/* Section header */}
      <div className={styles.sectionHeader}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <h3 className={styles.sectionTitle}>
          {titleOverride || t('progressGallery.title')}
        </h3>
        {photos.length > 0 && (
          <span className={styles.countBadge}>
            {photos.length}{maxPhotos < Infinity ? ` / ${maxPhotos}` : ''}
          </span>
        )}
        {canUpload && (
          <>
            <button
              className={styles.addBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || atLimit}
              title={atLimit ? t('progressGallery.limitReached', { max: maxPhotos }) : undefined}
            >
              + {t('progressGallery.addPhotos')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleUpload(e.target.files)}
            />
          </>
        )}
      </div>

      {/* Loading */}
      {loading && photos.length === 0 && (
        <div className={styles.emptySection}>
          <p className={styles.emptyHint}>{t('progressGallery.loading')}</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && photos.length === 0 && uploadCount === 0 && (
        <div className={styles.emptySection}>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="1.5"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <p className={styles.emptyTitle}>{emptyTitleOverride || t('progressGallery.noPhotos')}</p>
          <p className={styles.emptyHint}>{emptyHintOverride || t('progressGallery.noPhotosHint')}</p>
        </div>
      )}

      {/* Photo grid */}
      {(photos.length > 0 || uploadCount > 0) && (
        <div className={styles.photoGrid}>
          {/* Upload placeholders */}
          {uploadCount > 0 &&
            Array.from({ length: uploadCount }).map((_, i) => (
              <div
                key={`uploading-${i}`}
                className={`${styles.photoCard} ${styles.uploadingCard}`}
              >
                <div className={styles.photoThumb} />
                <div className={styles.uploadSpinner} />
              </div>
            ))}

          {/* Existing photos */}
          {photos.map((photo, idx) => {
            const isMenuOpen = menuOpenGuid === photo.mediaLinkGuid;
            const isEditing = editingCaptionGuid === photo.mediaLinkGuid;

            return (
              <div
                key={photo.mediaLinkGuid}
                className={styles.photoCard}
                onClick={() => {
                  if (!isEditing) setLightboxIndex(idx);
                }}
              >
                <img
                  className={styles.photoThumb}
                  src={mediaSrc(photo.thumbnailKey || photo.storageKey) ?? undefined}
                  alt={photo.label || ''}
                  loading="lazy"
                />

                {/* Three-dot menu button.
                    Hidden for status-sourced items — those are read-only
                    here; their owning status is the place to edit/delete
                    (DEV-1079). */}
                {canUpload && photo.source !== 'status' && (
                  <div
                    className={styles.menuAnchor}
                    ref={isMenuOpen ? menuRef : undefined}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className={`${styles.menuBtn} ${isMenuOpen ? styles.menuBtnOpen : ''}`}
                      onClick={() => setMenuOpenGuid(isMenuOpen ? null : photo.mediaLinkGuid)}
                      aria-label="Photo actions"
                    >
                      &#8943;
                    </button>

                    {isMenuOpen && (
                      <div className={styles.menuDropdown}>
                        {photo.label ? (
                          <>
                            <button
                              className={styles.menuItem}
                              onClick={() => {
                                setCaptionDraft(photo.label || '');
                                setEditingCaptionGuid(photo.mediaLinkGuid);
                                setMenuOpenGuid(null);
                              }}
                            >
                              {t('progressGallery.editCaption')}
                            </button>
                            <button
                              className={styles.menuItem}
                              onClick={() => handleRemoveCaption(photo.mediaLinkGuid)}
                            >
                              {t('progressGallery.removeCaption')}
                            </button>
                          </>
                        ) : (
                          <button
                            className={styles.menuItem}
                            onClick={() => {
                              setCaptionDraft('');
                              setEditingCaptionGuid(photo.mediaLinkGuid);
                              setMenuOpenGuid(null);
                            }}
                          >
                            {t('progressGallery.addCaption')}
                          </button>
                        )}
                        <div className={styles.menuDivider} />
                        <button
                          className={`${styles.menuItem} ${styles.menuItemDanger}`}
                          onClick={() => {
                            setMenuOpenGuid(null);
                            if (window.confirm(t('progressGallery.deleteConfirm'))) {
                              handleDelete(photo.mediaLinkGuid);
                            }
                          }}
                        >
                          {t('progressGallery.deletePhoto')}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Caption editor or static meta */}
                {isEditing ? (
                  <div
                    className={styles.captionEditor}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      className={styles.captionInput}
                      type="text"
                      value={captionDraft}
                      onChange={(e) => setCaptionDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveCaption(photo.mediaLinkGuid, captionDraft.trim());
                        } else if (e.key === 'Escape') {
                          setEditingCaptionGuid(null);
                        }
                      }}
                      placeholder={t('progressGallery.captionPlaceholder')}
                      autoFocus
                      maxLength={200}
                      disabled={savingCaption}
                    />
                    <button
                      className={styles.captionSaveBtn}
                      onClick={() => handleSaveCaption(photo.mediaLinkGuid, captionDraft.trim())}
                      disabled={savingCaption}
                    >
                      &#10003;
                    </button>
                  </div>
                ) : (
                  <div className={styles.photoMeta}>
                    {photo.label && (
                      <span className={styles.photoCaption}>{photo.label}</span>
                    )}
                    <span className={styles.photoDate}>{fmtDate(photo.createdAt)}</span>
                    {photo.source === 'status' && photo.sourceReportingDate && (
                      <span
                        className={styles.statusSourceChip}
                        title={t('progressGallery.fromStatusTooltip')}
                      >
                        {`${t('progressGallery.fromStatusPrefix')} ${fmtDate(photo.sourceReportingDate)}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Error display */}
      {error && <div className={styles.errorMsg}>{error}</div>}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={lightboxIndex}
          canDelete={canUpload}
          onDelete={handleDelete}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
